import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useMatch, useParams } from 'react-router-dom'
import {
  loadTradeDetail,
  loadTradeDocuments,
  loadTradeOverview,
} from 'app/state/ducks/trade-detail/thunks'
import { loadMasterTradeStream } from './activity-stream/activity-stream-daml-actions'

/** The Overview tab is the page's index route. */
export const TRADE_OVERVIEW_ROUTE = '/tradeentry/:tradeId'
/** The Sub-trade tab. */
export const TRADE_SUB_TRADE_ROUTE = '/tradeentry/:tradeId/subtrade'
/** The Documents tab. */
export const TRADE_DOCUMENTS_ROUTE = '/tradeentry/:tradeId/documents'
/** The Activity Stream tab. */
export const TRADE_ACTIVITY_ROUTE = '/tradeentry/:tradeId/activity'

/**
 * What a write on the Trade detail page refetches afterwards (ADR_LOS-030):
 * THIS trade, not the tenant, always as a fresh request.
 *
 * - `refreshOverview()` — the Overview view, whatever the route.
 * - `refreshDocuments()` — the Documents read (the Sub-trade read's records
 *   and lookups plus signable documents, funding-memo metadata and
 *   deliveries), whatever the route.
 * - `refreshTrade()` — what the open tab's read holds: the Overview view on
 *   Overview, the Documents read on Documents, the Sub-trade read on
 *   Sub-trade, and the Activity read on the Activity Stream (the trade, the
 *   header, the composer's sub trades and the notes — LOS-7095).
 *
 * The trade is the one in the URL; each resolves when its read has landed.
 */
export const useTradeRefresh = () => {
  const dispatch = useDispatch()
  const { tradeId } = useParams()
  const isOverviewTab = !!useMatch(TRADE_OVERVIEW_ROUTE)
  const isSubTradeTab = !!useMatch(TRADE_SUB_TRADE_ROUTE)
  const isDocumentsTab = !!useMatch(TRADE_DOCUMENTS_ROUTE)
  const isActivityTab = !!useMatch(TRADE_ACTIVITY_ROUTE)
  const refreshOverview = useCallback(
    () =>
      tradeId
        ? Promise.resolve(dispatch(loadTradeOverview(tradeId, { fresh: true }) as any))
        : Promise.resolve(),
    [dispatch, tradeId],
  )
  const refreshDocuments = useCallback(
    () =>
      tradeId
        ? Promise.resolve(dispatch(loadTradeDocuments(tradeId, { fresh: true }) as any))
        : Promise.resolve(),
    [dispatch, tradeId],
  )
  const refreshTrade = useCallback((): Promise<unknown> => {
    if (isOverviewTab) return refreshOverview()
    if (isSubTradeTab) {
      return tradeId
        ? Promise.resolve(dispatch(loadTradeDetail(tradeId, { fresh: true }) as any))
        : Promise.resolve()
    }
    if (isActivityTab) {
      // Through the stream's failure handling, so a failed refetch after a write toasts as
      // the tab's other reads do (review on #8472).
      return tradeId
        ? loadMasterTradeStream(tradeId, dispatch, { fresh: true })
        : Promise.resolve()
    }
    if (isDocumentsTab) return refreshDocuments()
    return Promise.resolve()
  }, [dispatch, tradeId, isOverviewTab, isSubTradeTab, isDocumentsTab, isActivityTab, refreshOverview, refreshDocuments])
  return {
    refreshTrade,
    refreshOverview,
    refreshDocuments,
    isOverviewTab,
    isSubTradeTab,
    isDocumentsTab,
    isActivityTab,
  }
}
